import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * HeaderComponent (legacy-safe)
 * - Sin private fields (#)
 * - Sin optional chaining (?.) ni nullish coalescing (??)
 * - Con guards para ResizeObserver / IntersectionObserver
 */
class HeaderComponent extends Component {
  constructor() {
    super();

    /** @type {string[]} */
    this.requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

    /** @type {number|null} */
    this._menuDrawerHiddenWidth = null;

    /** @type {IntersectionObserver|null} */
    this._intersectionObserver = null;

    /** @type {boolean} */
    this._offscreen = false;

    /** @type {number} */
    this._lastScrollTop = 0;

    /** @type {number|null} */
    this._timeout = null;

    /** @type {number|null} */
    this._scrollRafId = null;

    /** @type {ResizeObserver|null} */
    this._resizeObserver = null;

    // Bind handlers (para removeEventListener)
    this._handleOverflowMinimum = this._handleOverflowMinimum.bind(this);
    this._handleWindowScroll = this._handleWindowScroll.bind(this);
    this._updateScrollState = this._updateScrollState.bind(this);
  }

  /**
   * Crea (si existe) el ResizeObserver que mantiene --header-height
   */
  _initResizeObserver() {
    if (!('ResizeObserver' in window)) return;

    this._resizeObserver = new ResizeObserver((entries) => {
      const entry = entries && entries[0];
      if (!entry) return;

      // borderBoxSize puede variar según navegador
      let blockSize = null;
      if (entry.borderBoxSize && entry.borderBoxSize[0] && entry.borderBoxSize[0].blockSize != null) {
        blockSize = entry.borderBoxSize[0].blockSize;
      } else if (entry.contentRect && entry.contentRect.height != null) {
        blockSize = entry.contentRect.height;
      }

      if (blockSize == null) return;

      const roundedHeaderHeight = Math.round(blockSize);
      document.body.style.setProperty('--header-height', String(roundedHeaderHeight) + 'px');

      // Si el drawer estaba forzado a mostrarse por ancho, re-evaluar
      if (this._menuDrawerHiddenWidth && window.innerWidth > this._menuDrawerHiddenWidth) {
        this._updateMenuVisibility(false);
      }
    });
  }

  /**
   * Observa sticky state (si existe IntersectionObserver)
   * @param {boolean} alwaysSticky
   */
  _observeStickyPosition(alwaysSticky) {
    if (this._intersectionObserver) return;
    if (!('IntersectionObserver' in window)) return;

    const config = { threshold: alwaysSticky ? 1 : 0 };

    this._intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries && entries[0];
      if (!entry) return;

      const isIntersecting = entry.isIntersecting;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this._offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this._intersectionObserver.observe(this);
  }

  /**
   * Maneja overflowMinimum desde header-menu
   * @param {CustomEvent} event
   */
  _handleOverflowMinimum(event) {
    if (!event || !event.detail) return;
    this._updateMenuVisibility(!!event.detail.minimumReached);
  }

  /**
   * Actualiza visibilidad menu/drawer
   * @param {boolean} hideMenu
   */
  _updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this._menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this._menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  _handleWindowScroll() {
    if (this._scrollRafId !== null) return;

    this._scrollRafId = requestAnimationFrame(() => {
      this._scrollRafId = null;
      this._updateScrollState();
    });
  }

  _updateScrollState() {
    const stickyMode = this.getAttribute('sticky');
    if (!this._offscreen && stickyMode !== 'always') return;

    // scrollingElement no existe en algunos Safari viejos → fallback
    const scrollingEl = document.scrollingElement || document.documentElement || document.body;
    const scrollTop = scrollingEl && scrollingEl.scrollTop ? scrollingEl.scrollTop : 0;

    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this._lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this._lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this._offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this._lastScrollTop = scrollTop;
  }

  connectedCallback() {
    super.connectedCallback();

    // ResizeObserver
    this._initResizeObserver();
    if (this._resizeObserver) this._resizeObserver.observe(this);

    // Overflow menu event
    this.addEventListener('overflowMinimum', this._handleOverflowMinimum);

    // Sticky logic
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this._observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this._handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._intersectionObserver) this._intersectionObserver.disconnect();

    this.removeEventListener('overflowMinimum', this._handleOverflowMinimum);
    document.removeEventListener('scroll', this._handleWindowScroll);

    if (this._scrollRafId !== null) {
      cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;
  if (!('ResizeObserver' in window)) return;

  const resizeObserver = new ResizeObserver((entries) => {
    let headerGroupHeight = 0;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const target = entry.target;

      let blockSize = 0;
      if (entry.borderBoxSize && entry.borderBoxSize[0] && entry.borderBoxSize[0].blockSize != null) {
        blockSize = entry.borderBoxSize[0].blockSize;
      } else if (entry.contentRect && entry.contentRect.height != null) {
        blockSize = entry.contentRect.height;
      }

      // Replica la lógica original
      if (
        target !== header ||
        (header && header.hasAttribute('transparent') && header.parentElement && header.parentElement.nextElementSibling)
      ) {
        headerGroupHeight += blockSize;
      }
    }

    const rounded = Math.round(headerGroupHeight);
    document.body.style.setProperty('--header-group-height', String(rounded) + 'px');
  });

  if (header && header instanceof HTMLElement) {
    resizeObserver.observe(header);
  }

  // Observe hijos del header group
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element && element instanceof HTMLElement) {
      resizeObserver.observe(element);
    }
  }

  // Re-observar si cambian hijos
  const mutationObserver = new MutationObserver((mutations) => {
    for (let m = 0; m < mutations.length; m++) {
      const mutation = mutations[m];
      if (mutation.type === 'childList') {
        const kids = headerGroup.children;
        for (let i = 0; i < kids.length; i++) {
          const el = kids[i];
          if (el && el instanceof HTMLElement) {
            resizeObserver.observe(el);
          }
        }
      }
    }
  });

  mutationObserver.observe(headerGroup, { childList: true });
});